import { useEffect, useRef, useState, type FormEvent } from 'react'
import { ExternalLink, Music, Pause, Play, Search, X } from 'lucide-react'
import { UserAvatar } from '@/components/composites/user-avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useListening, useSearchMusic, useShare, videoIdOf, type SearchHit } from './api'

/**
 * The Music button: search a song and play it here, paste a link, and see what colleagues are listening to.
 *
 * Playing is YouTube's own embedded player. The panel is only moved out of sight when closed, never removed,
 * so the song carries on while people get on with their work. Sharing is what makes the track show to others,
 * and stopping clears it.
 */
export function MusicButton() {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [link, setLink] = useState('')
  const [playing, setPlaying] = useState<{ id: string; title: string; author: string; url: string } | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const team = useListening()
  const search = useSearchMusic(q)
  const share = useShare()

  // Click outside or Escape closes the panel (the music keeps playing).
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const me = team.data?.me
  const others = (team.data?.items ?? []).filter((i) => i.userId !== me)
  const mine = (team.data?.items ?? []).find((i) => i.userId === me)

  const play = (hit: { id: string; title: string; author: string; url: string }) => {
    setPlaying(hit)
    share.mutate({ url: hit.url, title: hit.title })
  }
  const playHit = (hit: SearchHit) => play(hit)
  const stop = () => {
    setPlaying(null)
    share.mutate({ url: null })
  }
  const submitLink = (e: FormEvent) => {
    e.preventDefault()
    const url = link.trim()
    if (!url) return
    const id = videoIdOf(url)
    // Shared either way; only YouTube links can play in the app.
    share.mutate({ url }, { onSuccess: (res) => {
      if (id && res.track) setPlaying({ id, title: res.track.title, author: res.track.author, url: res.track.url })
      setLink('')
    } })
  }

  return (
    <div ref={box} className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-label={playing ? `Music, playing ${playing.title}` : 'Music'}
        aria-expanded={open}
        title={playing ? `Playing: ${playing.title}` : 'Music: search, play and see what the team is listening to'}
        className={cn('h-9 gap-1.5 px-2.5', playing && 'bg-brand-soft text-brand-dark')}
      >
        <Music className="size-4" />
        {playing ? (
          <span className="flex h-3 items-end gap-0.5" aria-hidden>
            <span className="w-0.5 animate-pulse bg-brand-accent [height:60%]" />
            <span className="w-0.5 animate-pulse bg-brand-accent [animation-delay:150ms] [height:100%]" />
            <span className="w-0.5 animate-pulse bg-brand-accent [animation-delay:300ms] [height:40%]" />
          </span>
        ) : (
          <span className="hidden md:inline">Music</span>
        )}
      </Button>

      {/* Moved off-screen, not unmounted, when closed: unmounting would stop the song. */}
      <div
        className={cn('z-drawer absolute right-0 top-full mt-2 w-[24rem] max-w-[92vw] rounded-xl border border-border bg-card p-3 shadow-lg', !open && 'pointer-events-none fixed -left-[9999px] top-0 opacity-0')}
        aria-hidden={!open}
      >
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Music</p>
          <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="rounded p-1 text-text-muted hover:bg-muted"><X className="size-4" /></button>
        </div>

        {playing ? (
          <div className="mt-2 overflow-hidden rounded-lg border border-border">
            <iframe
              title={`Playing ${playing.title}`}
              src={`https://www.youtube-nocookie.com/embed/${playing.id}?autoplay=1&rel=0&playsinline=1`}
              allow="autoplay; encrypted-media; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              className="aspect-video w-full"
            />
            <div className="flex items-center gap-2 px-2.5 py-1.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{playing.title}</p>
                {playing.author ? <p className="truncate text-tiny text-text-muted">{playing.author}</p> : null}
              </div>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={stop}><Pause className="size-3.5" /> Stop &amp; stop sharing</Button>
            </div>
          </div>
        ) : mine ? (
          <p className="mt-2 rounded-md bg-muted px-2.5 py-1.5 text-tiny text-text-secondary">
            Sharing: {mine.title}.{' '}
            <button type="button" className="text-brand underline" onClick={() => share.mutate({ url: null })}>Stop sharing</button>
          </p>
        ) : null}

        <div className="mt-3 grid gap-1.5">
          <label htmlFor="music-q" className="text-tiny font-medium text-text-secondary">Search a song or artist</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-text-muted" />
            <Input id="music-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. lofi focus, Coldplay Yellow" maxLength={100} className="h-9 pl-8 text-sm" />
          </div>
          {q.trim().length >= 2 ? (
            <ul className="max-h-56 overflow-auto rounded-md border border-border">
              {search.isPending ? <li className="px-2.5 py-2 text-tiny text-text-muted">Searching…</li> : null}
              {search.data && !search.data.configured ? (
                <li className="px-2.5 py-2 text-tiny text-text-secondary">Search is not switched on yet. Paste a link below, and ask an admin to add the YouTube key.</li>
              ) : null}
              {search.data?.error ? <li className="px-2.5 py-2 text-tiny text-danger">{search.data.error}</li> : null}
              {search.data?.configured && !search.data.error && search.data.items.length === 0 ? <li className="px-2.5 py-2 text-tiny text-text-muted">Nothing found.</li> : null}
              {search.data?.items.map((hit) => (
                <li key={hit.id}>
                  <button type="button" onClick={() => playHit(hit)} className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted">
                    {hit.thumb ? <img src={hit.thumb} alt="" className="h-9 w-14 shrink-0 rounded object-cover" loading="lazy" /> : <span className="h-9 w-14 shrink-0 rounded bg-muted" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{hit.title}</span>
                      <span className="block truncate text-tiny text-text-muted">{hit.author}</span>
                    </span>
                    <Play className="size-4 shrink-0 text-brand" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <form onSubmit={submitLink} className="mt-3 grid gap-1.5">
          <label htmlFor="music-link" className="text-tiny font-medium text-text-secondary">Or paste a YouTube, YouTube Music or Spotify link</label>
          <div className="flex gap-1.5">
            <Input id="music-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" maxLength={300} className="h-9 text-sm" />
            <Button type="submit" size="sm" className="h-9" disabled={!link.trim() || share.isPending}>Share</Button>
          </div>
          <p className="text-micro text-text-muted">Spotify links are shared for others to open; only YouTube plays here.</p>
        </form>

        <div className="mt-3 border-t border-border pt-2">
          <p className="text-tiny font-medium text-text-secondary">Listening now</p>
          {others.length === 0 ? (
            <p className="py-2 text-tiny text-text-muted">Nobody is sharing a song right now.</p>
          ) : (
            <ul className="mt-1 max-h-48 overflow-auto">
              {others.map((i) => {
                const id = videoIdOf(i.url)
                return (
                  <li key={i.userId} className="flex items-center gap-2 py-1">
                    <UserAvatar person={{ id: i.userId, displayName: i.displayName, photoUrl: i.photoUrl }} size="xs" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm"><span className="font-medium">{i.displayName.split(' ')[0]}</span> · {i.title}</p>
                      {i.author ? <p className="truncate text-tiny text-text-muted">{i.author}</p> : null}
                    </div>
                    {id ? (
                      <button type="button" title="Play what they are playing" aria-label={`Play ${i.title}`} onClick={() => play({ id, title: i.title, author: i.author, url: i.url })} className="rounded p-1 text-brand hover:bg-muted"><Play className="size-4" /></button>
                    ) : null}
                    <a href={i.url} target="_blank" rel="noreferrer" title="Open in a new tab" aria-label={`Open ${i.title} in a new tab`} className="rounded p-1 text-text-muted hover:bg-muted"><ExternalLink className="size-4" /></a>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
